import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Chart } from "react-charts";
import moment from "moment";

import styles from "./results.scss";
import history from "../../history";
import { getIconUrl } from "../../services/asset";
import {
  HrCountryId,
  LocalStorageKey,
  RealEstatePricesUserTypeValues,
  UserRole
} from "../../models";
import { localStorage } from "../../services";
import { selectCategories } from "../App/selectors";
import { useResultsPollPopup } from "./useResultsPollPopup";
import { Statistics } from "../../services/api";
import { mapCategoryToEn } from "../../routes/LanguageMapper";
import { parsePriceString } from "../../services/utils";
import BreadcrumbBar from "../../components/BreadcrumbBar";
import HelmetComponent from "../../utils/HelmetHelper";
import {
  buildPricesResultsUrl,
  findPropertyType,
  LocationOption,
  LocationSlugMatch,
  PRICES_BASE_PATH,
  resolveLocationByPath
} from "./helpers";
import PricesSelectionForm from "./PricesSelectionForm";
import { AdsOnThisLocation } from "./AdsOnThisLocation";
import { selectUserInfo } from "../../routes/Auth/auth.ducks";
import { RealEstatePricesFilter } from "./RealEstatePricesFilter";
import ReactGA from "react-ga4";

const PAGE_SIZE = 12; // 12 quarters = 3 years


interface RouteMatch {
  params: { type: string; county: string; city?: string; settlement?: string, userType?: string };
}
interface LocationType {
  search: any;
}

interface MonthlyPriceData {
  date: string;
  priceM2: number;
}

interface OtherLocation {
  locationId?: string;
  locationName: string;
  // ready slug path (county/city/settlement) of the location
  locationRoutePath?: string | null;
  averagePriceM2: number;
}

interface RealEstatePriceStats {
  averagePriceM2: number;
  lowerRangePriceM2: number;
  higherRangePriceM2: number;
  adCount: number;
  monthlyAveragePricesM2: MonthlyPriceData[];
  otherLocations: OtherLocation[];
}

interface QuarterData {
  quarter: number;
  year: number;
  prices: number[];
  key: string;
}

interface ChartDatum {
  index: number;
  quarter: number;
  year: number;
  priceM2: number;
  label: string;
}

const capitalize = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const formatPrice = (value: number | null): string => (value == null ? "" : parsePriceString(Math.round(value)));
const formatPriceM2 = (value: number | null): string => (value == null ? "" : formatPrice(value) + " / m²");

const formatQuarterLabel = (quarter: number, year: number): string => "Q" + quarter + " " + year;

const formatTooltipQuarter = (quarter: number, year: number): string => quarter + ". kvartal " + year + ".";

const getPreviousMonthName = () => {
  const prevMonth = moment().subtract(1, "month");
  return capitalize(prevMonth.format("MMMM")) + " " + prevMonth.format("YYYY") + ".";
};

const MOM_THRESHOLD_PERCENT = 1;

const findEntryByYearMonth = (entries: MonthlyPriceData[], target: moment.Moment) => {
  const y = target.year();
  const m = target.month();
  for (let i = 0; i < entries.length; i++) {
    const d = moment(entries[i].date);
    if (d.year() === y && d.month() === m) return entries[i];
  }
  return null;
};

const computePriceDeltas = (stats: RealEstatePriceStats | null) => {
  if (!stats || stats.averagePriceM2 == null) return null;
  const monthly = stats.monthlyAveragePricesM2;
  if (!monthly || !monthly.length) return null;

  const previousMonth = moment().subtract(1, "month");
  const momRefMoment = moment().subtract(2, "month");
  const yoyRefMoment = moment().subtract(13, "month");

  const momEntry = findEntryByYearMonth(monthly, momRefMoment);
  const yoyEntry = findEntryByYearMonth(monthly, yoyRefMoment);

  const buildDelta = (entry: MonthlyPriceData | null, refMoment: moment.Moment, includeYear: boolean) => {
    if (!entry || !entry.priceM2) return null;
    const percent = ((stats.averagePriceM2 - entry.priceM2) / entry.priceM2) * 100;
    const name = capitalize(refMoment.format("MMMM"));
    const label = includeYear
      ? name + " " + refMoment.format("YYYY") + "."
      : name + ".";
    return { percent: percent, label: label };
  };

  const mom = buildDelta(momEntry, momRefMoment, momRefMoment.year() !== previousMonth.year());
  const yoy = buildDelta(yoyEntry, yoyRefMoment, true);

  if (!mom && !yoy) return null;
  return { mom: mom, yoy: yoy };
};

const formatDeltaPercent = (percent: number): string => {
  const rounded = Math.round(percent * 10) / 10;
  const sign = rounded > 0 ? "+" : "";
  return sign + rounded.toLocaleString("hr-HR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + " %";
};

const PriceDeltaRow = ({ delta }: { delta: { percent: number; label: string } }) => {
  const toneClass =
    delta.percent > 0 ? styles.priceDeltaUp :
      delta.percent < 0 ? styles.priceDeltaDown :
        styles.priceDeltaNeutral;
  return (
    <div className={styles.priceDelta + " " + toneClass}>
      {delta.percent > 0 && <span className={styles.priceDeltaArrow}>{"▲"}</span>}
      {delta.percent < 0 && <span className={styles.priceDeltaArrow}>{"▼"}</span>}
      <span className={styles.priceDeltaValue}>{formatDeltaPercent(delta.percent)}</span>
      <span className={styles.priceDeltaText}>{"u odnosu na " + delta.label}</span>
    </div>
  );
};

interface ResultsProps {
  match: RouteMatch;
  location: LocationType;
}

const Results = ({ match, location: { search } }: ResultsProps) => {
  const { type, county: countySlug, city: citySlug, settlement: settlementSlug } = match.params;

  const categories = useSelector(selectCategories);

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // 1-based page index. Defaults to the last page once data arrives.
  const [page, setPage] = useState(1);
  const user = useSelector(selectUserInfo);

  const params = new URLSearchParams(search);
  const userTypeParam = params.get("userType")
  const isAdmin = !!user && (user.role === UserRole.admin || user.role === UserRole.superAdmin);
  const selectedUserType = userTypeParam && isAdmin ? Number(userTypeParam) : RealEstatePricesUserTypeValues.ALL_USERS

  const propertyType = findPropertyType(type, categories);

  // Location from the url slugs, resolved through configuration/locations/resolve.
  // `undefined` while resolving, `null` when the path is unknown.
  const unresolvedLocation: LocationSlugMatch | null | undefined = undefined;
  const [location, setLocation] = useState(unresolvedLocation);

  useEffect(() => {
    let isCurrent = true;
    // Slugs changed: drop the previous location and go back to the skeleton so the
    // success block (and the ads widget) never renders against a stale/undefined location.
    setLocation(undefined);
    setLoading(true);
    setError(false);

    resolveLocationByPath(countySlug, citySlug, settlementSlug).then(resolved => {
      if (isCurrent) {
        setLocation(resolved || null);
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [countySlug, citySlug, settlementSlug]);

  // Save current URL location to localStorage when successfully parsed
  useEffect(() => {
    if (!location || !type || !location.countyId) return;

    localStorage.setItem(LocalStorageKey.realEstatePricesSelection, {
      propertyType: type,
      propertyLocation: {
        countryId: HrCountryId,
        countyId: location.countyId,
        cityId: location.cityId || null,
        settlementId: location.settlementId || null
      }
    });
  }, [location, type]);

  // Handle form submit from error fallback form
  const handleFormSubmit = (selectedPropertyType: string, selectedLocation: LocationOption) => {
    ReactGA.event({
      category: "Stranica prosječnih cijena nekretnina",
      action: "Click",
      label: "Promijenjeni parametri pretrage"
    });

    history.push(buildPricesResultsUrl(selectedPropertyType, selectedLocation.route));
  };

  useEffect(() => {
    // still resolving the location
    if (location === undefined) return;
    if (!categories || !categories.length) return;
    if (!propertyType || !location) {
      // Unknown slug — bounce back to selection page.
      history.replace(PRICES_BASE_PATH);
      return;
    }

    setLoading(true);
    setError(false);

    Statistics.realEstatePrice({
      Category: mapCategoryToEn(type, "real-estate"),
      CountyId: location.countyId,
      CityId: location.cityId,
      SettlementId: location.settlementId,
      userType: selectedUserType
    }).then(data => {
      if (!data) {
        setError(true);
        setLoading(false);
        return;
      }
      setStats(data);
      setLoading(false);
    });
  }, [location, categories, type, selectedUserType]);

  const fullLocationPath = location
    ? [location.countyName, location.cityName, location.settlementName].filter(Boolean).join(" > ")
    : "";

  const title = propertyType && fullLocationPath
    ? `${propertyType.label} - ${fullLocationPath}`
    : "Prosječne cijene nekretnina";

  // Aggregate monthly data into quarters
  const quarterlyData = useMemo(() => {
    if (!stats || !stats.monthlyAveragePricesM2 || !stats.monthlyAveragePricesM2.length) {
      return [];
    }
    const quarters: Record<string, QuarterData> = {};
    stats.monthlyAveragePricesM2.forEach((d: MonthlyPriceData) => {
      const date = moment(d.date);
      const quarter = Math.ceil((date.month() + 1) / 3);
      const year = date.year();
      const key = year + "-Q" + quarter;
      if (!quarters[key]) {
        quarters[key] = { quarter: quarter, year: year, prices: [], key: key };
      }
      quarters[key].prices.push(d.priceM2);
    });
    const result = Object.keys(quarters).sort().map((key: string) => {
      const q = quarters[key];
      const avgPrice = q.prices.reduce((a: number, b: number) => a + b, 0) / q.prices.length;
      return {
        quarter: q.quarter,
        year: q.year,
        priceM2: avgPrice,
        label: formatQuarterLabel(q.quarter, q.year)
      };
    });
    return result;
  }, [stats]);

  const priceDeltas = useMemo(() => computePriceDeltas(stats), [stats]);

  const numberOfPages = useMemo(() => {
    if (!quarterlyData.length) return 0;
    return Math.ceil(quarterlyData.length / PAGE_SIZE);
  }, [quarterlyData]);

  // Jump to the latest page whenever a fresh dataset arrives.
  useEffect(() => {
    if (numberOfPages > 0) setPage(numberOfPages);
  }, [numberOfPages]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const isFromIndexPortalParam = params.get("isFromIndexPortal")
    if (isFromIndexPortalParam) {
      ReactGA.event({
        category: "Cijene nekretnina widget",
        action: "Click",
        label: `search kliknut`
      });
    }

  }, []);

  // Slice a window of quarters for the current page
  const visibleQuarters = useMemo(() => {
    if (!quarterlyData.length) return [];
    const offset = PAGE_SIZE - ((quarterlyData.length % PAGE_SIZE) || PAGE_SIZE);
    const startIndex = page === 1 ? 0 : (page - 1) * PAGE_SIZE - offset;
    const endIndex = page * PAGE_SIZE - offset;
    return quarterlyData.slice(startIndex, endIndex);
  }, [quarterlyData, page]);

  const chartData = useMemo(() => {
    if (!visibleQuarters.length) return [];
    return [{
      label: "Cijena/m²",
      data: visibleQuarters.map((d, index: number): ChartDatum => ({
        index: index,
        quarter: d.quarter,
        year: d.year,
        priceM2: d.priceM2,
        label: d.label
      }))
    }];
  }, [visibleQuarters]);

  // Same condition that renders the <Chart /> below.
  const isChartVisible =
    !loading && !error && !!stats && chartData.length > 0 && chartData[0].data.length > 0;

  useResultsPollPopup(isChartVisible);

  const primaryAxis = useMemo(() => ({
    getValue: (d: ChartDatum) => d.index,
    formatters: {
      scale: (index: number) => {
        const d = visibleQuarters[index];
        return d ? d.label : "";
      },
      tooltip: (index: number) => {
        const d = visibleQuarters[index];
        return d ? formatTooltipQuarter(d.quarter, d.year) : "";
      }
    }
  }), [visibleQuarters]);

  const secondaryAxes = useMemo(() => [{
    getValue: d => d.priceM2,
    elementType: "area",
    formatters: { scale: formatPrice, tooltip: formatPrice }
  }], []);

  const pageRangeText = useMemo(() => {
    if (!visibleQuarters.length) return "";
    const first = visibleQuarters[0];
    const last = visibleQuarters[visibleQuarters.length - 1];
    return first.label + " - " + last.label;
  }, [visibleQuarters]);

  // Static content — render immediately during skeleton/success/error states
  // since it doesn't depend on fetched data.
  const infoCard = (
    <div className={styles.infoCard}>
      <div className={styles.cardTitle}>
        <span className={styles.infoMark}>{"i"}</span>
        {"Kako računamo prosječnu cijenu?"}
      </div>
      <p>
        Prikazana prosječna cijena po m² izračunava se na temelju oglasa koji su bili aktivni barem jedan dan tijekom odabranog mjeseca,
        za odabranu vrstu nekretnine i lokaciju.
      </p>
      <p>

        U obzir ulaze samo oglasi s upisanom cijenom i površinom, dok se očite anomalije i ekstremne vrijednosti
        isključuju kako bi rezultat bio što realniji. Prikazana vrijednost predstavlja prosjek cijena po m² svih
        oglasa koji su zadovoljili navedene uvjete.
      </p>
      <p>

        Pri izračunu se ne uzimaju u obzir stanje, opremljenost, starost ili druge karakteristike nekretnine,
        pa su moguće veće razlike među cijenama. Prikazane podatke zato treba koristiti kao informativni pokazatelj
        kretanja cijena, a ne kao preciznu procjenu vrijednosti pojedine nekretnine.
      </p>
      <p>

        Podaci se obrađuju jednom mjesečno, zbog čega prikazani prosjek predstavlja stanje
        za prethodni mjesec, a ne trenutno stanje aktivnih oglasa.
      </p>
    </div>
  );

  return (
    <React.Fragment>
      <HelmetComponent title={`${title} | INDEX OGLASI`} />
      <BreadcrumbBar
        className={styles.breadcrumb}
        routeLinks={[
          { title: "Prosječne cijene nekretnina", link: PRICES_BASE_PATH },
          { title }
        ]}
        category={"prosjecne-cijene-nekretnina"}
      />

      <div className={styles.container}>
        <div className={styles.wrapper}>
          <h1 className={styles.title}>{title}</h1>

          {loading && (
            <React.Fragment>
              <div className={styles.statsRow}>
                {[0, 1, 2].map(i => (
                  <div key={i} className={styles.statBox}>
                    <div className={`${styles.skeletonLoader} ${styles.skeletonLineLabel}`} />
                    <div className={`${styles.skeletonLoader} ${styles.skeletonLineBig}`} />
                  </div>
                ))}
              </div>
              <div className={styles.chartCard}>
                <div className={`${styles.skeletonLoader} ${styles.skeletonLineLabel}`} />
                <div className={styles.chartHolder}>
                  <div className={`${styles.skeletonLoader} ${styles.skeletonChart}`} />
                </div>
              </div>
              <div className={styles.bottomRow}>
                <div className={styles.otherLocations}>
                  <div className={`${styles.skeletonLoader} ${styles.skeletonLineLabel}`} />
                  {[0, 1, 2, 3, 4].map(i => (
                    <div key={i} className={`${styles.skeletonLoader} ${styles.skeletonRow}`} />
                  ))}
                </div>
                {infoCard}
              </div>
            </React.Fragment>
          )}

          {!loading && error && (
            <React.Fragment>
              <div className={styles.empty}>
                {"Trenutno nema dovoljno podataka za prikaz kretanja cijena za odabranu lokaciju i vrstu nekretnine."}
              </div>

              <PricesSelectionForm
                className={styles.errorFormCard}
                categories={categories || []}
                initialPropertyType={type}
                onSubmit={handleFormSubmit}
              />
              {isAdmin && (<RealEstatePricesFilter selectedUserType={selectedUserType} />)}

            </React.Fragment>
          )}

          {!loading && !error && stats && (
            <React.Fragment>
              <div style={{ marginBottom: 16 }}>
                <PricesSelectionForm
                  categories={categories || []}
                  initialPropertyType={type}
                  onSubmit={handleFormSubmit}
                />
              </div>
              <div className={styles.statsRow}>
                <div className={styles.statBox}>
                  <div className={styles.statLabel}>{"Prosjek za " + getPreviousMonthName()}</div>
                  <div className={styles.statBig}>
                    {formatPrice(stats.averagePriceM2)} <span className={styles.statUnit}>{"/ m²"}</span>
                  </div>
                  <div className={styles.statHint}>{"Izračunato prema aktivnim oglasima u prethodnom mjesecu."}</div>
                  {priceDeltas && (priceDeltas.yoy || (priceDeltas.mom && Math.abs(priceDeltas.mom.percent) >= MOM_THRESHOLD_PERCENT)) && (
                    <div className={styles.priceDeltaGroup}>
                      {priceDeltas.mom && Math.abs(priceDeltas.mom.percent) >= MOM_THRESHOLD_PERCENT && (
                        <PriceDeltaRow delta={priceDeltas.mom} />
                      )}
                      {priceDeltas.yoy && (
                        <PriceDeltaRow delta={priceDeltas.yoy} />
                      )}
                    </div>
                  )}
                </div>
                <div className={styles.statBox}>
                  <div className={styles.statLabel}>{"Raspon cijena za " + getPreviousMonthName()}</div>
                  <div className={styles.rangeRow}>
                    <span>{"od"}</span> <strong>{formatPriceM2(stats.lowerRangePriceM2)}</strong>
                  </div>
                  <div className={styles.rangeRow}>
                    <span>{"do"}</span> <strong>{formatPriceM2(stats.higherRangePriceM2)}</strong>
                  </div>
                  <div className={styles.statHint}>{"Najniža i najviša cijena po m² među oglasima uključenima u izračun."}</div>
                </div>
                <div className={styles.statBox}>
                  <div className={styles.statLabel}>{"Broj oglasa u izračunu"}</div>
                  <div className={styles.statBigDark}>{stats.adCount.toLocaleString("hr-HR")}</div>
                </div>
              </div>
              {isAdmin && (<RealEstatePricesFilter selectedUserType={selectedUserType} />)}

              {isChartVisible && (
                <div className={styles.chartCard}>
                  <div className={styles.chartTitle}>{"Prosječna cijena po kvartalima"}</div>
                  <div className={styles.pagination}>
                    <button
                      className={styles.paginationButton}
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      <img alt="" src={getIconUrl("chevron-left.svg")} />
                    </button>
                    <span className={styles.paginationText}>{pageRangeText}</span>
                    <button
                      className={styles.paginationButton}
                      disabled={page === numberOfPages}
                      onClick={() => setPage(page + 1)}
                    >
                      <img alt="" src={getIconUrl("chevron-left.svg")} />
                    </button>
                  </div>
                  <div className={styles.chartHolder}>
                    <Chart
                      options={{
                        data: chartData,
                        primaryAxis,
                        secondaryAxes,
                        defaultColors: ["#90BE6D"],
                        tooltip: {
                          render: props => {
                            const focusedDatum = props.focusedDatum;
                            if (!focusedDatum) return null;
                            const datum = focusedDatum.originalDatum;
                            return (
                              <div className={styles.tooltip}>
                                <div className={styles.tooltipDate}>
                                  {formatTooltipQuarter(datum.quarter, datum.year)}
                                </div>
                                <div className={styles.tooltipValue}>
                                  <span className={styles.tooltipLocation}>
                                    {(location && location.name) || ""}{":"}
                                  </span>
                                  <strong className={styles.tooltipPrice}>
                                    {formatPriceM2(datum.priceM2)}
                                  </strong>
                                </div>
                              </div>
                            );
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              )}

              <div className={styles.bottomRow}>
                {stats.otherLocations && stats.otherLocations.length > 0 && (
                  <div className={styles.otherLocations}>
                    <div className={styles.cardTitle}>{"Druge lokacije"}</div>
                    <table className={styles.locationsTable}>
                      <tbody>
                        {stats.otherLocations.map((loc: OtherLocation) => {
                          const targetUrl = loc.locationRoutePath
                            ? buildPricesResultsUrl(type, loc.locationRoutePath)
                            : null;
                          return (
                            <tr key={loc.locationId || loc.locationName}>
                              <td>
                                {targetUrl ? (
                                  <Link className={styles.locationLink} to={targetUrl}>
                                    {loc.locationName}
                                  </Link>
                                ) : (
                                  loc.locationName
                                )}
                              </td>
                              <td className={styles.priceCell}>{formatPriceM2(loc.averagePriceM2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {infoCard}
              </div>
              <AdsOnThisLocation
                location={location}
                type={type}
              />
            </React.Fragment>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default Results;



// WEBPACK FOOTER //
// ./src/containers/RealEstatePricesPage/Results.tsx